import { RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { logger } from '@/utils/logger/logger';

import { LastRowSectionMap } from './build-last-row-section-map';
import {
  DEFAULT_NESTED_PAGINATION_LOG_TAG,
  RegisterRowRef,
  SectionPaginationApi,
} from './nested-pagination.types';

interface UseSectionIntersectionObserversParams {
  /** Ref to the table's scroll container ([data-scroll-container]); used as observer root. */
  scrollContainerRef: RefObject<HTMLElement | null>;
  /** Map of "last loaded row id of a section" → its section id (rebuilt each render). */
  lastRowToSection: LastRowSectionMap;
  sectionPagination: SectionPaginationApi;
  rootMargin?: string;
  logTag?: string;
}

interface UseSectionIntersectionObserversResult {
  /** Callback ref the table attaches to every row; observes only section-tail rows. */
  registerRowRef: RegisterRowRef;
}

/**
 * A single multi-target IntersectionObserver that watches the LAST loaded row of each
 * expanded, not-yet-fully-loaded section and triggers that section's `fetchNextPage`.
 *
 * Why one shared observer (vs N): it roots at the table scroll container so visibility is
 * measured against the table viewport — not the page — so a section loads only when ITS
 * tail scrolls into view, never from rows below it. The observer is created once and kept
 * stable:
 *  - the root is resolved into STATE (a bare ref is null on the first renders and ref
 *    mutations don't re-run effects);
 *  - `lastRowToSection` and `sectionPagination` are mirrored into refs so the intersect
 *    callback never changes identity and the observer is not torn down every render.
 */
export const useSectionIntersectionObservers = ({
  scrollContainerRef,
  lastRowToSection,
  sectionPagination,
  rootMargin = '150px',
  logTag = DEFAULT_NESTED_PAGINATION_LOG_TAG,
}: UseSectionIntersectionObserversParams): UseSectionIntersectionObserversResult => {
  // Latest props mirrored into refs → stable intersect callback.
  const lastRowToSectionRef = useRef(lastRowToSection);
  lastRowToSectionRef.current = lastRowToSection;
  const sectionPaginationRef = useRef(sectionPagination);
  sectionPaginationRef.current = sectionPagination;

  // Registered row elements, by row id.
  const rowElsRef = useRef(new Map<string, HTMLTableRowElement>());
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Resolve the scroll container into state so the observer effect runs once it exists.
  // The container is null on the first renders (the table shows a loading spinner before
  // it mounts), so we re-resolve whenever the tail set changes — by which point the table
  // is mounted — as well as on mount. The `prev === el` guard makes this idempotent.
  const [rootEl, setRootEl] = useState<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const el = scrollContainerRef.current;
    setRootEl(prev => (prev === el ? prev : el));
  }, [scrollContainerRef, lastRowToSection]);

  useEffect(() => {
    if (!rootEl) {
      logger.debug(logTag, 'observer not created yet — scroll container unresolved');
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const rowId = (entry.target as HTMLElement).dataset.sectionRowId;
          if (!rowId) continue;

          const section = lastRowToSectionRef.current.get(rowId);
          if (!section) {
            logger.debug(logTag, 'tail row intersected but not a section tail anymore', { rowId });
            continue;
          }

          const snapshot = sectionPaginationRef.current.getSection(section.sectionId);
          logger.debug(logTag, 'section tail intersected', {
            rowId,
            sectionId: section.sectionId,
            hasNextPage: snapshot?.hasNextPage,
            isFetchingNextPage: snapshot?.isFetchingNextPage,
          });

          if (snapshot?.hasNextPage && !snapshot.isFetchingNextPage) {
            logger.debug(logTag, 'fetching next page', { sectionId: section.sectionId });
            sectionPaginationRef.current.fetchNextPage(section.sectionId);
          }
        }
      },
      { root: rootEl, rootMargin, threshold: 0 }
    );

    observerRef.current = observer;
    logger.debug(logTag, 'observer created', { observedRows: rowElsRef.current.size });

    // Observe any already-registered rows that are currently section tails.
    rowElsRef.current.forEach((el, rowId) => {
      if (lastRowToSectionRef.current.has(rowId)) observer.observe(el);
    });

    return () => {
      observer.disconnect();
      observerRef.current = null;
    };
  }, [rootEl, rootMargin, logTag]);

  // Re-sync which rows are observed whenever the section-tail set changes: only rows
  // that are a section tail (present in lastRowToSection) need observing.
  useEffect(() => {
    const observer = observerRef.current;
    if (!observer) return;

    let observed = 0;
    rowElsRef.current.forEach((el, rowId) => {
      if (lastRowToSection.has(rowId)) {
        observer.observe(el);
        observed++;
      } else {
        observer.unobserve(el);
      }
    });
    logger.debug(logTag, 'resynced observed tails', {
      tailIds: [...lastRowToSection.keys()],
      registeredRows: rowElsRef.current.size,
      nowObserving: observed,
    });
  }, [lastRowToSection, logTag]);

  const registerRowRef = useCallback((rowId: string, el: HTMLTableRowElement | null) => {
    const prev = rowElsRef.current.get(rowId);
    if (prev && prev !== el) {
      observerRef.current?.unobserve(prev);
    }

    if (el) {
      el.dataset.sectionRowId = rowId;
      rowElsRef.current.set(rowId, el);
      // Only observe rows that are currently a section tail.
      if (observerRef.current && lastRowToSectionRef.current.has(rowId)) {
        observerRef.current.observe(el);
      }
    } else {
      rowElsRef.current.delete(rowId);
    }
  }, []);

  return { registerRowRef };
};
